import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { taskApi } from "../services/api";
import { fetchAllPages } from "./utils";

/**
 * Self-contained fetch hook for the Tasks resource.
 *
 * Accepts a `projectScope` argument:
 *   - "all"     → no project filter; fetch everything
 *   - "none"    → fetch all, filter locally to tasks where project == null
 *                 (the backend does not support ?project=none; it parses
 *                  the value as int() inside try/except and silently
 *                  ignores non-numeric values)
 *   - <id>      → pass ?project=<id> through to the backend
 *
 * Always orders by position ascending, matching the TaskViewSet default.
 * Exposes refresh + mutation helpers that trigger a re-fetch on success.
 */
const useTasks = (projectScope) => {
  const [rawTasks, setRawTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // "all" and "none" both map to "no project filter" at the API level;
  // Number("all") and Number("none") are NaN, so a single finite-check
  // covers the three cases without enumerating strings.
  const queryParams = useMemo(() => {
    const numericId = Number(projectScope);
    return Number.isFinite(numericId)
      ? { project: numericId, ordering: "position" }
      : { ordering: "position" };
  }, [projectScope]);

  // `background: true` refetches without toggling `loading`, so consumers
  // keep rendering the (stale) data they already have instead of unmounting
  // into a spinner. Every refetch that follows a mutation uses it — the
  // spinner is for "we have nothing to show yet", not "reconciling".
  // Fetches now span several round-trips, so two of them can be in flight
  // at once (a project-scope change, or a mutation refetch racing the
  // initial load) and there is no guarantee they resolve in the order they
  // started. Without this guard a slower EARLIER fetch lands last and
  // overwrites the newer result — a board that stays wrong until a reload.
  const requestRef = useRef(0);

  // Resolves true when this fetch landed, false when it failed, and
  // undefined when a newer fetch superseded it (that one carries the
  // truth). The live board commits its pulse stamp only on true, so a
  // failed refetch is retried instead of forgotten (review round, #327).
  const fetchTasks = useCallback(
    async ({ background = false } = {}) => {
      const request = ++requestRef.current;
      if (!background) setLoading(true);
      setError(null);
      try {
        // Follow `next`: page 1 alone silently caps the board at 12 tasks.
        const { items } = await fetchAllPages((page) =>
          taskApi.getAll({ ...queryParams, page }),
        );
        if (request !== requestRef.current) return undefined;
        setRawTasks(items);
        return true;
      } catch (err) {
        if (request !== requestRef.current) return undefined;
        setError(err);
        return false;
      } finally {
        if (!background && request === requestRef.current) setLoading(false);
      }
    },
    [queryParams],
  );

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Client-side filter for "No project" scope since the backend can't express it.
  const tasks = useMemo(() => {
    if (projectScope === "none") {
      return rawTasks.filter((task) => task.project == null);
    }
    return rawTasks;
  }, [rawTasks, projectScope]);

  const createTask = useCallback(
    async (data) => {
      const response = await taskApi.create(data);
      await fetchTasks({ background: true });
      return response.data;
    },
    [fetchTasks],
  );

  const updateTask = useCallback(
    async (id, data) => {
      const response = await taskApi.update(id, data);
      await fetchTasks({ background: true });
      return response.data;
    },
    [fetchTasks],
  );

  const deleteTask = useCallback(
    async (id) => {
      await taskApi.delete(id);
      await fetchTasks({ background: true });
    },
    [fetchTasks],
  );

  return {
    tasks,
    loading,
    error,
    refresh: fetchTasks,
    createTask,
    updateTask,
    deleteTask,
  };
};

export default useTasks;
